import React, { useCallback, useEffect, useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { BsFillPersonFill } from "react-icons/bs";
import secureLocalStorage from "react-secure-storage";
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";

import {
  AuthLogin,
  GetUsersInfo,
  PostNewPassword,
  ResetPassword,
  SendCode,
} from "../../data/services";
import { captchaKeys } from "../../shared/enums/captchaKeys";
import SenhaComponent from "./Senha";
import NewSenhaComponent from "./NewSenha";

import {
  Page,
  LeftPanel,
  BrandRow,
  Headline,
  RightPanel,
  Card,
  FooterLogo,
  Title,
  Tabs,
  TabBtn,
  Field,
  PasswordFieldWrapper,
  PasswordToggle,
  Hint,
  Row,
  Btn,
  LinkLike,
  Alert,
  Spinner,
  CaptchaStyle,
  LeftInner,
  TextSurface,
  LoginForm,
  PasswordFlowForm,
} from "./Login.styles";
import {
  normalizeMatricula,
  safeSwitch,
} from "./services/helpers";
import { canSubmitColab, canSubmitForn } from "./services/guards";

type Tab = "COLAB" | "FORN";

const Login: React.FC = () => {
  const [tab, setTab] = useState<Tab>("COLAB");
  const [showPassword, setShowPassword] = useState(false);

  // fluxos
  const [firstAccess, setFirstAccess] = useState(false);
  const [savingFirstAccess, setSavingFirstAccess] = useState(false);
  const [consultEmail, setConsultEmail] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [modalAlterarSenha, setModalAlterarSenha] = useState(false);

  // formulário
  const [username, setUsername] = useState(""); // matricula (colab) ou email (forn)
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [senhaValida, setSenhaValida] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: "",
    confirmPassword: "",
  });

  const [res, setRes] = useState<any>();
  const [error, setError] = useState<
    { error: boolean; message: string } | false
  >(false);
  const [errorCode, setErrorCode] = useState({ error: false, text: "" });
  const [user, setUser] = useState<any>("");
  const [captcha, setCaptcha] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const submitLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const ok =
        tab === "COLAB"
          ? canSubmitColab(username, password)
          : canSubmitForn(username, password);

      if (!ok || isSubmitting) return;

      setIsSubmitting(true);

      const type = tab === "COLAB" ? "GP" : "FORN";
      const profile =
        tab === "COLAB" ? "usuarioColaborador" : "usuarioFornecedor";

      try {
        const response = await AuthLogin({ username, password, profile, setVal: setRes });
        secureLocalStorage.setItem("access_token", response.data.access_token);
        await GetUsersInfo(setUser, username, type);
        setRes(response);
      } catch (err) {
        if (secureLocalStorage.getItem("access_token")) {
          setError({ error: true, message: "Não foi possível carregar seu cadastro. Tente entrar novamente." });
        }
        secureLocalStorage.removeItem("access_token");
        setIsSubmitting(false);
      }
    },
    [tab, username, password, isSubmitting]
  );

  useEffect(() => {
    if (
      res ||
      error ||
      firstAccess ||
      modalAlterarSenha ||
      consultEmail ||
      resetPassword
    ) {
      setIsSubmitting(false);
    }
  }, [res, error, firstAccess, modalAlterarSenha, consultEmail, resetPassword]);

  useEffect(() => {
    if (
      res?.response?.data?.idUsuario &&
      res?.response?.data?.mensagem === "Acesso negado, precisa mudar a senha"
    ) {
      setFirstAccess(true);
    }

    if (res?.response?.data?.mensagem) {
      const msg = res.response.data.mensagem;
      if (
        msg === "Email não existe no cadastro Notifier" ||
        msg === "Erro ao logar usuário interno" ||
        msg === "Matrícula não existe no cadastro Notifier" ||
        res?.response?.status === 401
      ) {
        setError({ error: true, message: msg });
      }
      if (res?.status === 502) {
        secureLocalStorage.clear();
        setError({
          error: true,
          message:
            "Ocorreu um problema ao realizar o login. Atualize a página e tente novamente!",
        });
      }
    }

    if (!!user) {
      const u = user[0];
      if (u) {
        secureLocalStorage.setItem("login", u.login);
        secureLocalStorage.setItem("nomeUsuario", u.nomeUsuario);
        secureLocalStorage.setItem("profile", u.perfilAcesso);
        secureLocalStorage.setItem("idFabrica", u.idFabrica);
        secureLocalStorage.setItem("idUsuario", u.idUsuario);
        secureLocalStorage.setItem("tipo", u.tipo);
        secureLocalStorage.setItem("email", u.email);
        secureLocalStorage.setItem("unidade", u.unidade);
      }
    }

    if (res?.data?.access_token) {
      secureLocalStorage.setItem("access_token", res.data.access_token);
      secureLocalStorage.setItem("username", username);
      window.location.href = "/home";
    }
  }, [res, user, username]);

  // primeiro acesso
  const confirmFirstAccessCode = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!code)
        return setErrorCode({
          error: true,
          text: "Por favor, insira o código",
        });
      setErrorCode({ error: false, text: "" });
      setFirstAccess(false);
      setModalAlterarSenha(true);
    },
    [code]
  );

  // reset senha (fornecedor)
  const requestResetCode = async () => {
    try {
      if (!username) return;
      await SendCode({ email: username }, setError);
      setConsultEmail(false);
      setResetPassword(true);
    } catch (err) {
      setErrorCode({ error: true, text: `${err}` });
    }
  };

  const doResetPassword = async () => {
    try {
      if (!senhaValida) return alert("Se atente aos requisitos da senha");
      await ResetPassword(
        { email: username, verificationCode: code, novaSenha: password },
        setError
      );
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setErrorCode({ error: true, text: `${err}` });
    }
  };

  // alterar senha (primeiro acesso)
  const confirmNewPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (savingFirstAccess || !senhaValida) return;
      if (newPassword.password !== newPassword.confirmPassword) {
        return setErrorCode({
          error: true,
          text: "As senhas não são iguais. Por favor, tente novamente.",
        });
      }
      setSavingFirstAccess(true);
      setErrorCode({ error: false, text: "" });
      try {
        const response = await PostNewPassword({
          idUsuario: res?.response?.data?.idUsuario,
          confirmationCode: code,
          senhaAntiga: password,
          novaSenha: newPassword.password,
        });
        if (response?.status !== 200) {
          return setErrorCode({
            error: true,
            text:
              response?.data?.mensagem || "Ocorreu um erro. Tente novamente.",
          });
        }
        setErrorCode({
          error: false,
          text: "Nova senha confirmada com sucesso!",
        });
        setTimeout(() => (window.location.href = "/"), 1500);
      } catch (err: any) {
        const msg = err?.response?.data?.mensagem;
        setErrorCode({ error: true, text: msg || "Erro ao alterar senha." });
      } finally {
        setSavingFirstAccess(false);
      }
    },
    [code, newPassword, password, res?.response?.data?.idUsuario, savingFirstAccess, senhaValida]
  );

  const returnToLogin = () => {
    setFirstAccess(false);
    setModalAlterarSenha(false);
    setConsultEmail(false);
    setResetPassword(false);
    setRes(undefined);
    setError(false);
    setErrorCode({ error: false, text: "" });
    setCode("");
    setPassword("");
    setNewPassword({ password: "", confirmPassword: "" });
    setSenhaValida(false);
    setShowPassword(false);
  };

  const FirstAccessHelp = (
    <Hint role="note" style={{ marginTop: 16 }}>
      <span aria-hidden="true">ⓘ </span>
      Precisa de ajuda? Entre em contato com o administrador da ferramenta.
      Se a conta já foi confirmada, você pode tentar novamente ou voltar ao login e usar Esqueci minha senha.
    </Hint>
  );

  /** ===== Forms ===== */
  const ColaboradorForm = (
    <LoginForm onSubmit={submitLogin}>
      <Hint>
        Se for <b>Terceiro</b>, use <b>C</b> + 8 dígitos (ex.: <b>C12345678</b>
        ).
        <br /> Caso contrário, use apenas 8 dígitos.
      </Hint>

      <Field
        placeholder="Matrícula"
        value={username}
        onChange={(e) => {
          setError(false);
          setUsername(normalizeMatricula(e.target.value));
        }}
        hasError={!!error}
        autoComplete="username"
        aria-label="Matrícula do colaborador"
      />

      <PasswordFieldWrapper>
      <Field
        type={showPassword ? "text" : "password"}
        placeholder="Senha"
        value={password}
        onChange={(e) => {
          setError(false);
          setPassword(e.target.value);
        }}
        autoComplete="current-password"
        aria-label="Senha"
      />
      <PasswordToggle
        type="button"
        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
        title={showPassword ? "Ocultar senha" : "Mostrar senha"}
        onClick={() => setShowPassword((visible) => !visible)}
      >
        {showPassword ? <AiFillEyeInvisible aria-hidden="true" /> : <AiFillEye aria-hidden="true" />}
      </PasswordToggle>
      </PasswordFieldWrapper>

      <Row>
        <Btn
          type="submit"
          disabled={!canSubmitColab(username, password) || isSubmitting}
        >
          {isSubmitting ? <Spinner aria-label="carregando" /> : "Entrar"}
        </Btn>
      </Row>

      {error && error.error && <Alert tone="danger">{error.message}</Alert>}
    </LoginForm>
  );

  const FornecedorForm = (
    <LoginForm onSubmit={submitLogin}>
      <Field
        type="email"
        placeholder="E-mail do fornecedor"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        hasError={!!error}
        autoComplete="email"
        aria-label="E-mail"
      />
      <PasswordFieldWrapper>
      <Field
        type={showPassword ? "text" : "password"}
        placeholder="Senha"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        aria-label="Senha"
      />
      <PasswordToggle
        type="button"
        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
        title={showPassword ? "Ocultar senha" : "Mostrar senha"}
        onClick={() => setShowPassword((visible) => !visible)}
      >
        {showPassword ? <AiFillEyeInvisible aria-hidden="true" /> : <AiFillEye aria-hidden="true" />}
      </PasswordToggle>
      </PasswordFieldWrapper>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "6px 0 12px",
        }}
      >
        <LinkLike onClick={safeSwitch(() => setConsultEmail(true))}>
          Esqueci minha senha
        </LinkLike>
      </div>

      <Row>
        <Btn
          type="submit"
          disabled={!canSubmitForn(username, password) || isSubmitting}
        >
          {isSubmitting ? <Spinner aria-label="carregando" /> : "Entrar"}
        </Btn>
      </Row>

      {error && error.error && <Alert tone="danger">{error.message}</Alert>}

      <div className="grecaptcha-badge">
        <GoogleReCaptcha onVerify={(val) => setCaptcha(!!val)} />
      </div>
    </LoginForm>
  );

  const PrimeiroAcessoForm = (
    <PasswordFlowForm onSubmit={confirmFirstAccessCode}>
      <Title>Primeiro acesso</Title>
      <Hint>Etapa 1 de 2 · Código de confirmação</Hint>
      <p
        style={{
          color: "#6b7280",
          fontSize: 14,
          lineHeight: 1.55,
          marginTop: -6,
        }}
      >
        Digite o código recebido no e-mail de cadastro. Ele será validado junto com a nova senha na próxima etapa.
      </p>
      <Field
        placeholder="Código recebido por e-mail"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Row>
        <Btn variant="light" onClick={returnToLogin}>Voltar ao login</Btn>
        <Btn type="submit">Continuar</Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
      {FirstAccessHelp}
    </PasswordFlowForm>
  );

  const ResetEmailForm = (
    <PasswordFlowForm onSubmit={(e) => e.preventDefault()}>
      <Title>Redefinir senha</Title>
      <p
        style={{
          color: "#6b7280",
          fontSize: 14,
          lineHeight: 1.55,
          marginTop: -6,
        }}
      >
        Informe seu e-mail para enviarmos um código de verificação.
      </p>
      <Field
        type="email"
        placeholder="Seu e-mail"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <Row>
        <Btn variant="light" onClick={safeSwitch(() => setConsultEmail(false))}>
          Voltar
        </Btn>
        <Btn onClick={safeSwitch(requestResetCode)}>Validar e-mail</Btn>
      </Row>
      {error && (error as any).error && (
        <Alert tone="danger">{(error as any).message}</Alert>
      )}
    </PasswordFlowForm>
  );

  const ResetPasswordForm = (
    <PasswordFlowForm onSubmit={(e) => e.preventDefault()}>
      <Title>Redefinir senha</Title>
      <Field
        placeholder="Código recebido"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <SenhaComponent validaSenha={setSenhaValida} senhaAtual={setPassword} />
      <Row>
        <Btn
          variant="light"
          onClick={safeSwitch(() => {
            setResetPassword(false);
            setConsultEmail(true);
          })}
        >
          Voltar
        </Btn>
        <Btn onClick={safeSwitch(doResetPassword)} disabled={!senhaValida}>
          Salvar nova senha
        </Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
    </PasswordFlowForm>
  );

  const AlterarSenhaPrimeiroAcessoForm = (
    <PasswordFlowForm onSubmit={confirmNewPassword}>
      <Title>Definir nova senha</Title>
      <Hint>Etapa 2 de 2 · Escolha sua senha de acesso</Hint>
      <NewSenhaComponent
        initialPassword={newPassword}
        validaSenha={setSenhaValida}
        senhaAtual={setNewPassword}
      />
      <Row>
        <Btn variant="light" disabled={savingFirstAccess} onClick={() => {
          setErrorCode({ error: false, text: "" });
          setModalAlterarSenha(false);
          setFirstAccess(true);
        }}>Voltar ao código</Btn>
        <Btn type="submit" disabled={!senhaValida || savingFirstAccess}>
          {savingFirstAccess ? "Salvando…" : "Confirmar"}
        </Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
      <LinkLike as="button" type="button" disabled={savingFirstAccess} onClick={returnToLogin} style={{ marginTop: 12, border: 0, padding: 0, background: "transparent" }}>Voltar ao login</LinkLike>
      {FirstAccessHelp}
    </PasswordFlowForm>
  );

  const renderCurrentForm = () => {
    if (firstAccess) return PrimeiroAcessoForm;
    if (modalAlterarSenha) return AlterarSenhaPrimeiroAcessoForm;
    if (consultEmail) return ResetEmailForm;
    if (resetPassword) return ResetPasswordForm;
    return tab === "COLAB" ? ColaboradorForm : FornecedorForm;
  };

  return (
    <GoogleReCaptchaProvider
      language="pt-BR"
      reCaptchaKey={captchaKeys.USERS_CAPTCHA_KEY}
    >
      <CaptchaStyle />
      <Page>
        <LeftPanel bg="/assets/img/ita2.jpeg">
          <LeftInner>
            <TextSurface>
              <BrandRow>
                <div className="brand-name">Notifier · Grupo Petrópolis</div>
              </BrandRow>

              <Headline>Portal de Não Conformidades de Embalagens</Headline>
            </TextSurface>
          </LeftInner>
        </LeftPanel>

        <RightPanel>
          <Card>
            <Title>Login</Title>

            {/* Tabs só na tela principal */}
            {!firstAccess &&
              !consultEmail &&
              !resetPassword &&
              !modalAlterarSenha && (
                <Tabs role="tablist" aria-label="Tipo de acesso">
                  <TabBtn
                    active={tab === "COLAB"}
                    onClick={safeSwitch(() => {
                      setTab("COLAB");
                      setShowPassword(false);
                      setError(false);
                    })}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <BsFillPersonFill /> Colaborador
                    </span>
                  </TabBtn>
                  <TabBtn
                    active={tab === "FORN"}
                    onClick={safeSwitch(() => {
                      setTab("FORN");
                      setShowPassword(false);
                      setError(false);
                    })}
                  >
                    @ Fornecedor
                  </TabBtn>
                </Tabs>
              )}

            {renderCurrentForm()}

            <FooterLogo>
              <img
                className="logo-petropolis"
                src="/assets/img/logo-petropolis.png"
                alt="Petrópolis"
              />
            </FooterLogo>
          </Card>
        </RightPanel>
      </Page>
    </GoogleReCaptchaProvider>
  );
};

export default Login;
